import {
  RefObject,
  useCallback,
  useContext,
  useEffect,
  useMemo,
  useRef,
  useState,
} from "react"
import { setPageTitle } from "./App"
import {
  Form as FormikForm,
  Formik,
  FormikProps,
  FieldArray,
  Field,
  FieldProps,
} from "formik"
import {
  SolutionContext,
  IsSentContext,
  applyConfig,
  defaultFormValues,
  isDefault,
} from "./models/Form"
import { Box, Button, FormGroup, Grid, Typography } from "@mui/material"
import { SolutionField, SolutionConfigContext } from "./form/SolutionField"
import { BinaryQuestion } from "./form/Question"
import { useNavigate, useParams } from "react-router-dom"
import { hasAntiGlare } from "./models/Customization"
import { IPrintedObject, defaultLogo } from "./models/Printing"
import { ProductType } from "./models/Product"
import { thicknessToString, typeOThicknessToLayers } from "./models/Glass"
import { ISolution, fillOldData, getSolutionConfig } from "./models/Solution"
import Drawing, { DrawingHandle } from "./Drawing"
import { HeadTab } from "./form/HeadTab"
import { NextButton } from "./form/NextButton"
import { PreviousButton } from "./form/PreviousButton"
import { FormPageHeader } from "./form/FormPageHeader"
import { CornerRadiusField } from "./form/CornerRadius"
import { BorderSpacing } from "./form/BorderSpacing"
import { PairedInput } from "./form/PairedInput"
import { PlusButton } from "./form/PlusButton"
import { Separator } from "./form/Separator"
import errorFields from "./form/ErrorFields"
import useWindowSize from "./windowSize"
import { TailDetailInput } from "./form/TailDetail"
import { OtherSettings } from "./form/OtherSettings"
import { GlassWH } from "./form/GlassWidthHeight"
import { LayerORdering } from "./form/LayerOrdering"
import { generateLayers, GlassOrder, GlassOrderStrings } from "./models/Layers"
import { ObjectForm } from "./form/ObjectForm"
import GlassDrawing from "./DrawingGlass"
import { zeroIRadius } from "./models/Common"
import { languages } from "./i18n"
import { useTranslation } from "react-i18next"
import { CopyDefaultSensor } from "./form/DefaultSensor"

interface IFormPage {
  id: number
  title: string
  attribute: keyof ISolution
  adminOnly: boolean
}

interface IFormProps {
  admin: boolean
  test?: boolean
}

export const scrollbarSX = {
  "&::-webkit-scrollbar, & *::-webkit-scrollbar": {
    backgroundColor: "#0000",
    width: "5px",
  },
  "&::-webkit-scrollbar-track-piece": {
    backgroundColor: "#0000",
    border: "1px solid #dfe0f2",
    borderRight: "0px",
    borderRadius: 8,
  },
  "&::-webkit-scrollbar-track-piece:end ": {
    marginBottom: "25px",
  },
  "&::-webkit-scrollbar-track-piece:start ": {
    marginTop: "25px",
  },
  "&::-webkit-scrollbar-thumb, & *::-webkit-scrollbar-thumb": {
    borderRadius: 8,
    backgroundColor: "#1a0549",
    minHeight: 24,
    border: "0px solid white",
    height: "10px",
  },
  "&::-webkit-scrollbar-thumb:focus, & *::-webkit-scrollbar-thumb:focus": {
    backgroundColor: "#1a0549",
  },
  "&::-webkit-scrollbar-thumb:active, & *::-webkit-scrollbar-thumb:active": {
    backgroundColor: "#1a0549",
  },
  "&::-webkit-scrollbar-thumb:hover, & *::-webkit-scrollbar-thumb:hover": {
    backgroundColor: "#1a0549",
  },
}

export default function Form({ admin, test }: IFormProps) {
  const { i18n } = useTranslation()
  const { identifier, revision } = useParams()

  useEffect(() => setPageTitle("Form"), [])
  const drawingRef = useRef<DrawingHandle>(null) // Create a ref for Drawing component
  const drawingRefGlass0 = useRef<DrawingHandle>(null)
  const drawingRefGlass1 = useRef<DrawingHandle>(null)
  const drawingRefGlass2 = useRef<DrawingHandle>(null)
  const groupSx = {
    display: "flex",
    flexDirection: "column",
    "> *": {
      margin: "0 0 20px 0",
      "&:last-child": {
        margin: "0 0 0 0",
      },
    },
    mt: "2em",
  }
  const primaryGroupSx = {
    ...groupSx,
    "& > .MuiFormLabel-root": { fontSize: "150%" },
  }

  let ref = useRef<FormikProps<ISolution> | null>(null)
  const solutionContext = useContext(SolutionContext)
  const isSentContext = useContext(IsSentContext)
  const freshForm = isDefault(solutionContext)

  const [key, setKey] = useState(0)

  // define utility variables that are not part of the ISolution
  const [withPrinting, setWithPrinting] = useState<boolean>(
    freshForm ? false : solutionContext.printing?.withPrinting === true
  )
  const [withLogo, setWithLogo] = useState<boolean>(
    freshForm ? false : solutionContext.printing?.withLogo === true
  )
  const [withGlass, setWithGlass] = useState<boolean>(
    freshForm ? true : solutionContext.glass.withGlass === true
  )
  const [withSensor, setWithSensor] = useState<boolean>(
    freshForm ? false : solutionContext.sensor.withSensor === true
  )
  const [formPage, setFormPage] = useState<number>(0) // index, not id
  const [maxVisitedFormPage, setMaxVisitedFormPage] = useState<number>(0) // index, not id
  const [technicalDataReady, setTechnicalDataReady] = useState<boolean>(false)
  const [showCoverGlasses, setShowCoverGlasses] = useState<boolean>(false)
  const [finalSolution, setFinalSolution] = useState<ISolution>(
    structuredClone(defaultFormValues)
  )

  // duplicate selected form data in state for correct rerendering and effects
  const [productType, setProductType] = useState<ProductType>(
    solutionContext.product.type
  )
  const [tailDetail, setTailDetail] = useState<string | null>(null)
  const [tailFileName, setTailName] = useState<string | null>(null)

  const { fixedValues, constraints } = useMemo(
    () => getSolutionConfig(productType),
    [productType]
  )

  const pages = useMemo(
    () =>
      withGlass
        ? ([
            { id: 0, title: "Product", attribute: "product", adminOnly: false },
            { id: 1, title: "Glass", attribute: "glass", adminOnly: false },
            {
              id: 2,
              title: "Glass Customization",
              attribute: "customization",
              adminOnly: false,
            },
            {
              id: 3,
              title: "Glass Printing",
              attribute: "printing",
              adminOnly: false,
            },
            { id: 4, title: "Sensor", attribute: "sensor", adminOnly: false },
            { id: 6, title: "Layers", attribute: "layers", adminOnly: false },
            {
              id: 7,
              title: "Additional Notes",
              attribute: "product",
              adminOnly: false,
            },
            {
              id: 8,
              title: "Customer",
              attribute: "customer",
              adminOnly: false,
            },
            //{ id: 8, title: "Status", attribute: "status", adminOnly: true },
          ] as IFormPage[])
        : ([
            { id: 0, title: "Product", attribute: "product", adminOnly: false },
            { id: 1, title: "Glass", attribute: "glass", adminOnly: false },
            { id: 2, title: "Sensor", attribute: "sensor", adminOnly: false },
            {
              id: 7,
              title: "Additional Notes",
              attribute: "product",
              adminOnly: false,
            },
            {
              id: 8,
              title: "Customer",
              attribute: "customer",
              adminOnly: false,
            },
            //{ id: 5, title: "Status", attribute: "status", adminOnly: true },
          ] as IFormPage[]),
    [withGlass]
  )

  //Update withs
  useEffect(() => {
    const values = ref.current?.values
    if (values === undefined) return

    values.glass.withGlass = withGlass
    values.sensor.withSensor = withSensor
    values.printing.withPrinting = withPrinting
  }, [withGlass, withPrinting, withSensor])

  useEffect(() => {
    if (!ref.current) return
    applyConfig(ref.current, { fixedValues, constraints })
  }, [productType, fixedValues, constraints])

  useEffect(() => {
    const fetchData = async (identifier: string, revision: string) => {
      console.log("Loading drawing", identifier)
      const result = await fetch(`/api/drawing/${identifier}/${revision}`)
      if (result.status === 200) {
        const data = await result.json()
        await ref.current?.setValues(data)
        if (ref.current) fillOldData(data)
        setWithPrinting(data.printing?.withPrinting === true)
        setWithLogo(data.printing?.withLogo === true)
        setWithGlass(data.glass.withGlass === true)
        setWithSensor(data.sensor.withSensor === true)
        setMaxVisitedFormPage(6)
        setFormPage(0)
        Object.assign(solutionContext, data)
        console.log("Filled values:", solutionContext)
        setTechnicalDataReady(true)
      }
    }

    if (identifier !== undefined && revision !== undefined) {
      fetchData(identifier, revision)
    }
  }, [identifier, revision, solutionContext])

  // add missing data sections so that form can use them
  if (solutionContext.printing === null)
    solutionContext.printing = { ...defaultFormValues.printing! }
  if (
    solutionContext.customization &&
    solutionContext.customization.antiGlareType === null
  )
    solutionContext.customization.antiGlareType =
      defaultFormValues.customization!.antiGlareType

  const navigate = useNavigate()

  const sendToDB = useCallback(async () => {
    if (drawingRef.current) {
      const base64PDFData = await drawingRef.current.getPDF()

      const frontPDF = await drawingRefGlass0.current?.getPDF()
      const middlePDF = await drawingRefGlass1.current?.getPDF()
      const rearPDF = await drawingRefGlass2.current?.getPDF()

      if (base64PDFData === null) {
        console.log("base64PDFData is null")
        return
      }

      fetch("/api/drawing", {
        method: "POST",
        headers: {
          Accept: "application/json",
          "Content-Type": "application/json",
        },
        body: JSON.stringify({
          ...solutionContext,
          pdf: base64PDFData,
          frontPDF: frontPDF,
          middlePDF: middlePDF,
          rearPDF: rearPDF,
        }),
      })
      console.log("Drawing sent to server.")
    } else {
      console.log("Drawing not available")
    }
  }, [solutionContext])

  const changePage = useCallback(
    (currentPage: number, backwards: boolean) => {
      //if layerOrdering is not in pages, values.layers needs to be generated
      if (!withGlass && currentPage === 2 && ref.current) {
        ref.current.values.layers = generateLayers(
          ref.current.values.layers,
          ref.current.values.glass,
          ref.current.values.sensor,
          ref.current.values.customization,
          ref.current.values.product.type
        )
      }
      for (let key in errorFields) {
        if (errorFields[key] === true) return false
      }
      const newPage = backwards ? currentPage - 1 : currentPage + 1
      if (newPage >= 0 && newPage < pages.length) {
        setFormPage(newPage)
        setMaxVisitedFormPage(Math.max(newPage, maxVisitedFormPage))
      }
    },
    [pages, maxVisitedFormPage, withGlass]
  )

  useEffect(() => {
    //separated from technical data filled(), bcs
    //width, heigth, thickness are needed to be correct before layers tab
    //and technical data filled() is called only before final drawing
    if (ref.current === null) return

    const values = ref.current.values
    if (
      values.product.type === ProductType.FortouchO &&
      !values.glass.independentWH
    ) {
      values.glass.height[1] = values.glass.height[0]
      values.glass.height[2] = values.glass.height[0]

      values.glass.width[1] = values.glass.width[0]
      values.glass.width[2] = values.glass.width[0]

      values.glass.centralOffsetX = [0, 0, 0]
      values.glass.centralOffsetY = [0, 0, 0]
    }

    if (
      values.product.type === ProductType.FortouchO &&
      values.glass.thickness !== -1
    ) {
      //values.glass.width[1] = values.glass.width[0]
      //values.glass.height[1] = values.glass.height[0]

      const t = typeOThicknessToLayers(values.glass.thickness)
      values.glass.coverGlass = [t.front, 0.3, t.rear]
    }

    if (values.product.type !== ProductType.FortouchO) {
      values.glass.height[1] = 0
      values.glass.height[2] = 0

      values.glass.width[1] = 0
      values.glass.width[2] = 0
      const t =
        values.glass.thickness !== -1
          ? values.glass.thickness
          : values.glass.coverGlass[0]
      values.glass.coverGlass = [t, 0, 0]
      values.glass.centralOffsetX = [0, 0, 0]
      values.glass.centralOffsetY = [0, 0, 0]
    }
  }, [formPage])

  const onTechnicalDataFilled = useCallback(() => {
    if (ref.current !== null) {
      const values = ref.current.values

      if (values.printing) {
        values.printing.withPrinting = withPrinting
      }

      if (values.printing) {
        values.printing.withLogo = withLogo
      }

      values.glass.withGlass = withGlass
      values.sensor.withSensor = withSensor
      values.sensor.cable.detail = tailDetail

      if (values.product.type === ProductType.FortouchO)
        values.customization.otherSettings.PF = false

      if (values.printing) {
        values.printing.objects.forEach((obj, i) => {
          if (obj.wImg && !obj.img64) {
            obj.wImg = false
          }
          if (obj.wImg) {
            obj.isCircle = false
            obj.isHole = false
            obj.isTransparent = false
            obj.width = 0
          }
        })
      }

      if (values.sensor.tail.centralOffset === undefined) {
        values.sensor.tail.centralOffset = 0
      }

      values.printing.objects.forEach((obj) => {
        if (obj.positioning.centralOffsetX === undefined) {
          obj.positioning.centralOffsetX = 0
        }

        if (obj.positioning.centralOffsetY === undefined) {
          obj.positioning.centralOffsetY = 0
        }
      })

      if (values.customization && !hasAntiGlare(values.customization.surface))
        values.customization.antiGlareType = null

      //if type != type O cornes[1] and corners[2] have to be zero
      if (values.product.type !== ProductType.FortouchO) {
        values.customization.corners[1] = zeroIRadius()
        values.customization.corners[2] = zeroIRadius()
      } else if (!values.customization.independentCornes) {
        values.customization.corners[1] = values.customization.corners[0]
        values.customization.corners[2] = values.customization.corners[0]
      }

      Object.assign(solutionContext, values)
      setTechnicalDataReady(true)
      if (pages[formPage].id === 7) setFinalSolution(structuredClone(values))

      changePage(formPage, false)
    }
  }, [
    changePage,
    formPage,
    solutionContext,
    tailDetail,
    withGlass,
    withLogo,
    withPrinting,
    withSensor,
    pages,
  ])

  const onSubmit = useCallback(
    (values: ISolution) => {
      console.log("Submitting")
      Object.assign(solutionContext, values)
      isSentContext.sent = true
      setKey(key + 1) // enforces drawing component rerender

      // FIXME: Drawing component is not rerendered immediately, so this is a quick and dirty fix
      setTimeout(() => {
        sendToDB()
        console.log("Technical drawing submitted.")
        isSentContext.sent = false
        navigate("/confirm")
      }, 1000)
    },
    [solutionContext, isSentContext, key, sendToDB, navigate]
  )

  const download = async (ref: RefObject<DrawingHandle | null>) => {
    if (ref.current) {
      const base64PDFData = await ref.current.getPDF()

      if (base64PDFData === null) {
        console.log("base64PDFData is null")
        return
      }

      downloadBase64PDF(base64PDFData)
    }
  }

  const downloadBase64PDF = (base64Data: string, fileName = "download.pdf") => {
    console.log("DOWNOLOADING")
    const link = document.createElement("a")
    link.href = `data:application/pdf;base64,${base64Data}`
    link.download = fileName
    document.body.appendChild(link)
    link.click()
    document.body.removeChild(link)
  }

  const canChangePage = useCallback(
    (currentPage: number, backwards: boolean) => {
      const newPage = backwards ? currentPage - 1 : currentPage + 1
      return newPage >= 0 && newPage < pages.length
    },
    [pages]
  )

  const menuButtonSx = useCallback((i: number) => formPage === i, [formPage])

  const size: { width: number; height: number } = useWindowSize()
  const direction = size.width < 1100 ? "column" : "row"
  const formWidth = size.width < 1100 ? size.width - 140 : size.width / 3
  let drawingWidth =
    size.width < 1100 ? size.width - 140 : size.width - (150 + formWidth)
  if (isSentContext.sent) {
    drawingWidth = 1122
  }
  //const formHeight     = size.width < 1100 ? 900 : ((drawingWidth > 900) ? 900 : drawingWidth) / Math.sqrt(2)
  const formHeight = size.width < 1100 ? 900 : drawingWidth / Math.sqrt(2)
  const inFormHeight = formHeight - 200
  const formWidthSX = { width: `${formWidth}px` }
  const formHeightSX =
    size.width < 1100
      ? { maxHeight: `${formHeight}px` }
      : { height: `${formHeight}px` }
  const inFormHeightSX =
    size.width < 1100
      ? { maxHeight: `${inFormHeight}px` }
      : { height: `${inFormHeight}px` }

  const allCoverGlass = !withGlass
    ? []
    : ref.current?.values.product.type === ProductType.FortouchO
    ? [
        <GlassDrawing
          key={key}
          ref={drawingRefGlass0}
          admin={admin}
          renderEmpty={!technicalDataReady}
          width={drawingWidth}
          glassOrder={GlassOrder.Front}
          solutionFinal={finalSolution}
          test={test}
        />,
        <GlassDrawing
          key={key}
          ref={drawingRefGlass1}
          admin={admin}
          renderEmpty={!technicalDataReady}
          width={drawingWidth}
          glassOrder={GlassOrder.Middle}
          solutionFinal={finalSolution}
          test={test}
        />,
        <GlassDrawing
          key={key}
          ref={drawingRefGlass2}
          admin={admin}
          renderEmpty={!technicalDataReady}
          width={drawingWidth}
          glassOrder={GlassOrder.Rear}
          solutionFinal={finalSolution}
          test={test}
        />,
      ]
    : [
        <GlassDrawing
          key={key}
          ref={drawingRefGlass0}
          admin={admin}
          renderEmpty={!technicalDataReady}
          width={drawingWidth}
          glassOrder={GlassOrder.Front}
          solutionFinal={finalSolution}
          test={test}
        />,
      ]

  return (
    <div>
      {test && (
        <Button variant="contained" onClick={() => download(drawingRef)}>
          download from form
        </Button>
      )}
      {test && (
        <Button variant="contained" onClick={() => download(drawingRefGlass0)}>
          download front glass
        </Button>
      )}
      {test && (
        <Button variant="contained" onClick={() => download(drawingRefGlass1)}>
          download middle glass
        </Button>
      )}
      {test && (
        <Button variant="contained" onClick={() => download(drawingRefGlass2)}>
          download rear glass
        </Button>
      )}
      <Box
        sx={{
          display: "flex",
          flexDirection: "row",
          justifyContent: "space-between",
          alignItems: "center",
        }}
      >
        <Box
          sx={{
            display: "flex",
            flexWrap: "wrap",
            marginLeft: "50px",
            padding: 2,
          }}
        >
          {pages.map((page, i) => (
            <div key={i}>
              <HeadTab
                disabled={freshForm && i > maxVisitedFormPage}
                index={i}
                isActive={menuButtonSx(i)}
                onClick={() => {
                  for (let key in errorFields) {
                    if (errorFields[key] === true) return
                  }
                  setFormPage(i)
                }}
                text={page.title}
              />
            </div>
          ))}

          {/*!freshForm && <Button variant="text" type="submit" sx={menuButtonSx(pages.length)}
                disabled={freshForm && maxVisitedFormPage !== pages.length}>Drawing</Button>*/}
        </Box>

        <Box sx={{ marginInlineEnd: "25px" }}>
          <BinaryQuestion
            value={i18n.language === languages.en.workingName}
            text={undefined}
            labels={[languages.en.workingName, languages.cs.workingName]}
            onClicks={[
              () => {
                i18n.changeLanguage(languages.en.workingName)
              },
              () => {
                i18n.changeLanguage(languages.cs.workingName)
              },
            ]}
          />
        </Box>
      </Box>
      <Grid container direction={direction}>
        <Grid item>
          <Box
            sx={{
              ...formWidthSX,
              ...formHeightSX,
              border: "1px solid #dfe0f2",
              marginBlockEnd: "30px",
              marginInlineStart: "70px",
              marginInlineEnd: "50px",
              borderRadius: "35px",
              backgroundColor: "#f7fbff",
            }}
          >
            <SolutionConfigContext.Provider
              value={{ fixedValues, constraints }}
            >
              <Formik
                innerRef={(newRef) => {
                  ref.current = newRef
                  if (newRef) setProductType(newRef.values.product.type)
                }}
                initialValues={solutionContext}
                enableReinitialize={true}
                onSubmit={onSubmit}
              >
                <FormikForm>
                  <Box sx={{ margin: "3em auto", padding: 0 }}>
                    <Box sx={{ paddingLeft: "40px" }}>
                      {" "}
                      <FormPageHeader title={pages[formPage].title} />{" "}
                    </Box>
                    <Box
                      sx={{
                        backgroundColor: "#0000",
                        overflow: "auto",
                        padding: "0 40px 0 40px",
                        //width: "calc(min(100%, 500px) - 32px)",
                        ...scrollbarSX,
                        ...inFormHeightSX,
                      }}
                    >
                      {pages[formPage].title === "Product" && (
                        <FormGroup sx={primaryGroupSx}>
                          <SolutionField name="product.type" /> <Separator />
                          <SolutionField name="product.usage" /> <Separator />
                          <SolutionField name="product.pieces" /> <Separator />
                          <SolutionField name="product.title" /> <Separator />
                          <SolutionField name="product.drawingNumber" />{" "}
                          <Separator />
                          <SolutionField name="product.backView" />
                        </FormGroup>
                      )}

                      {pages[formPage].attribute === "glass" && (
                        <FormGroup sx={primaryGroupSx}>
                          <BinaryQuestion
                            value={withGlass}
                            text="Do you want to use glass?"
                            labels={["Yes", "No"]}
                            onClicks={[
                              () => {
                                setWithGlass(true)
                              },
                              () => {
                                setWithGlass(false)
                              },
                            ]}
                          />

                          {withGlass && (
                            <>
                              <Separator />
                              <GlassWH productType={productType} />
                              <SolutionField
                                name="glass.thickness"
                                valueToString={
                                  ref.current
                                    ? (value) =>
                                        thicknessToString(
                                          ref.current!.values.glass,
                                          value
                                        )
                                    : undefined
                                }
                              />
                            </>
                          )}
                        </FormGroup>
                      )}

                      {pages[formPage].attribute === "customization" && (
                        <FormGroup sx={primaryGroupSx}>
                          {withGlass && (
                            <>
                              <SolutionField name="customization.surface" />{" "}
                              <Separator />
                              <CornerRadiusField
                                productType={productType}
                              />{" "}
                              <Separator />
                              <SolutionField name="customization.cutouts" />
                              <SolutionField name="customization.holes" />{" "}
                              <Separator />
                              <OtherSettings
                                type={
                                  ref.current?.values.product.type ||
                                  ProductType.FortouchC
                                }
                              />
                            </>
                          )}
                        </FormGroup>
                      )}

                      {pages[formPage].attribute === "printing" && (
                        <FormGroup sx={primaryGroupSx}>
                          <BinaryQuestion
                            value={withPrinting}
                            text="Should the glass have a printed frame?"
                            labels={["Yes", "No"]}
                            onClicks={[
                              () => setWithPrinting(true),
                              () => {
                                setWithPrinting(false)
                                setWithLogo(false)
                              },
                            ]}
                          />

                          {withPrinting && (
                            <>
                              <Separator />
                              <SolutionField
                                name="printing.onGlass"
                                values={GlassOrderStrings(
                                  ref.current?.values.product.type
                                )}
                              />
                              <Separator />
                              <BorderSpacing
                                label="Frame Thickness (mm)"
                                topSolutionFiledName="printing.frameThickness.t"
                                leftSolutionFiledName="printing.frameThickness.l"
                                rightSolutionFiledName="printing.frameThickness.r"
                                bottomSolutionFiledName="printing.frameThickness.b"
                              />{" "}
                              <Separator />
                              <SolutionField name="printing.color" />
                              <SolutionField name="printing.doublePrint" />
                              <Separator />
                              <BinaryQuestion
                                value={withLogo}
                                text="Should we print any logo, pictogram or hole on it?"
                                labels={["Yes", "No"]}
                                onClicks={[
                                  () => setWithLogo(true),
                                  () => setWithLogo(false),
                                ]}
                              />
                            </>
                          )}

                          {withLogo && (
                            <>
                              <FieldArray
                                name="printing.objects"
                                render={(objectArr) => (
                                  <>
                                    {objectArr.form.values.printing.objects.map(
                                      (objVal: IPrintedObject, oi: number) => (
                                        <ObjectForm
                                          oi={oi}
                                          objectArrayHelpers={objectArr}
                                          objectVlues={objVal}
                                          key={oi}
                                        />
                                      )
                                    )}
                                    <PlusButton
                                      onClick={() => {
                                        const i =
                                          objectArr.form.values.printing.objects
                                            .length
                                        objectArr.push({
                                          ...defaultLogo,
                                          title: `Object ${i + 1}`,
                                        })
                                      }}
                                      label="Add object"
                                    />
                                  </>
                                )}
                              />
                            </>
                          )}
                        </FormGroup>
                      )}

                      {pages[formPage].attribute === "sensor" && (
                        <FormGroup sx={primaryGroupSx}>
                          <Field
                            component={(
                              fieldProps: FieldProps<number, ISolution>
                            ) => (
                              <BinaryQuestion
                                value={withSensor}
                                text="Do you want to use sensor?"
                                labels={["Yes", "No"]}
                                onClicks={[
                                  () => {
                                    setWithSensor(true)
                                  },
                                  () => {
                                    setWithSensor(false)
                                  },
                                ]}
                              />
                            )}
                          />

                          {withSensor && (
                            <>
                              <CopyDefaultSensor />
                              <Separator />
                              <SolutionField name="sensor.diagonal" />{" "}
                              <Separator />
                              <PairedInput
                                bigLabel="Active area (mm)"
                                elements={[
                                  <SolutionField
                                    name="sensor.activeArea.width"
                                    disabled={false}
                                  />,
                                  <SolutionField
                                    name="sensor.activeArea.height"
                                    disabled={false}
                                  />,
                                ]}
                              />{" "}
                              <Separator />
                              <BorderSpacing
                                label="Passive area (mm)"
                                topSolutionFiledName="sensor.passiveArea.t"
                                leftSolutionFiledName="sensor.passiveArea.l"
                                rightSolutionFiledName="sensor.passiveArea.r"
                                bottomSolutionFiledName="sensor.passiveArea.b"
                              />{" "}
                              <Separator />
                              <SolutionField name="sensor.thickness" />{" "}
                              <Separator />
                              <PairedInput
                                bigLabel="Sensor offset"
                                elements={[
                                  <SolutionField name="sensor.offset.x" />,
                                  <SolutionField name="sensor.offset.y" />,
                                ]}
                              />{" "}
                              <Separator />
                              <PairedInput
                                bigLabel="Tail (mm)"
                                elements={[
                                  <SolutionField name="sensor.tail.attachment" />,
                                  <SolutionField name="sensor.tail.centralOffset" />,
                                  <SolutionField name="sensor.tail.width" />,
                                  <SolutionField name="sensor.tail.length" />,
                                ]}
                              />{" "}
                              <Separator />
                              <PairedInput
                                bigLabel="Flexible calble (mm)"
                                elements={[
                                  <SolutionField name="sensor.cable.height" />,
                                  <TailDetailInput
                                    setDetail={setTailDetail}
                                    imgName={tailFileName}
                                    setImgName={setTailName}
                                  />,
                                ]}
                              />
                            </>
                          )}
                        </FormGroup>
                      )}

                      {pages[formPage].attribute === "layers" && (
                        <FormGroup sx={primaryGroupSx}>
                          {ref.current?.values && (
                            <LayerORdering
                              layers={generateLayers(
                                ref.current.values.layers,
                                ref.current.values.glass,
                                ref.current.values.sensor,
                                ref.current.values.customization,
                                ref.current.values.product.type
                              )}
                            />
                          )}
                        </FormGroup>
                      )}

                      {pages[formPage].title === "Additional Notes" && (
                        <FormGroup sx={primaryGroupSx}>
                          {admin && (
                            <Box sx={{ marginBottom: "2rem" }}>
                              <Typography margin="0 0 17px 0">
                                As admin, you can change notes generated by app
                              </Typography>

                              <SolutionField name="product.adminNotes" />
                            </Box>
                          )}

                          <Typography margin="0 0 17px 0">
                            Feel free to add anything else or specify something
                            in more detail.
                          </Typography>

                          <SolutionField name="product.notes" />
                        </FormGroup>
                      )}

                      {pages[formPage].title === "Customer" && (
                        <FormGroup sx={primaryGroupSx}>
                          <Typography margin="0 0 17px 0">
                            Please leave us your contact info so we can process
                            your request more easily.
                          </Typography>
                          <SolutionField name="customer.company" />{" "}
                          <Separator />
                          <SolutionField name="customer.creator" />{" "}
                          <Separator />
                          <SolutionField name="customer.email" />
                        </FormGroup>
                      )}
                    </Box>

                    <Box
                      sx={{
                        mt: "2em",
                        display: "grid",
                        gridTemplateColumns: "1fr 1fr",
                        padding: "0 40px 0 40px",
                      }}
                    >
                      {canChangePage(formPage, true) ? (
                        <PreviousButton
                          onClick={() => changePage(formPage, true)}
                        />
                      ) : (
                        <Button sx={{ visibility: "hidden" }} />
                      )}

                      {canChangePage(formPage, false) &&
                        pages[formPage].id !== 7 &&
                        pages[formPage].id !== 8 && (
                          <NextButton
                            text="next step"
                            onClick={() => changePage(formPage, false)}
                          />
                        )}

                      {pages[formPage].id === 7 && (
                        <NextButton
                          onClick={() => onTechnicalDataFilled()}
                          text={
                            !ref.current?.values.glass.withGlass &&
                            !ref.current?.values.sensor.withSensor
                              ? "drawing is empty"
                              : "next step"
                          }
                          disabled={
                            !ref.current?.values.glass.withGlass &&
                            !ref.current?.values.sensor.withSensor
                          }
                        />
                      )}

                      {pages[formPage].id === 8 && (
                        <NextButton
                          onClick={() => {
                            console.log("Submit button clicked")
                          }}
                          submit={true}
                          text="Submit"
                        />
                      )}
                    </Box>
                  </Box>
                </FormikForm>
              </Formik>
            </SolutionConfigContext.Provider>
          </Box>
        </Grid>
        <Grid item sx={{ width: `${drawingWidth}px` }}>
          {size.width > 1100 && (
            <Drawing
              key={key}
              ref={drawingRef}
              admin={admin}
              renderEmpty={!technicalDataReady}
              width={drawingWidth}
              solutionFinal={finalSolution}
              test={test}
            ></Drawing>
          )}
          {size.width <= 1100 && (
            <Box sx={{ marginInlineStart: "70px" }}>
              <Drawing
                key={key}
                ref={drawingRef}
                admin={admin}
                renderEmpty={!technicalDataReady}
                width={drawingWidth}
                solutionFinal={finalSolution}
                test={test}
              />
            </Box>
          )}

          {allCoverGlass.length > 0 && (
            <Box>
              <Box sx={{ justifyContent: "end", display: "flex" }}>
                <Button
                  onClick={() => setShowCoverGlasses(!showCoverGlasses)}
                  sx={{
                    textTransform: "none",
                    color: "white",
                    backgroundColor: "#250048",
                    marginBottom: "10px",
                    padding: "10px",
                    borderRadius: "999px",
                    "&:hover": {
                      backgroundColor: "#250048",
                    },
                  }}
                >
                  {showCoverGlasses
                    ? "hide coverglass layers"
                    : "show coverglass layers"}
                </Button>
              </Box>
              {showCoverGlasses && allCoverGlass}
            </Box>
          )}
        </Grid>
      </Grid>
    </div>
  )
}
